import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Flex,
  Indicator,
  Paper,
  Text,
} from "@mantine/core";
import { IconBellFilled } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  REALTIME_DOMAINS,
  useRealtime,
  useRealtimeDomain,
} from "@lib/realtime";
import { formatDate, formatTime } from "@util/dates";
import alertNavigationPath, {
  chatConversationIdFromAlert,
} from "./alertNavigationPath";
import { useChat } from "@components/Chat";

const ALERTS_CHANNEL = { channel: "AlertChannel" };

function alertStamp(value) {
  if (!value) return "";
  return `${formatDate(value)} ${formatTime(value)}`;
}

function alertUserName(alert) {
  if (alert?.user_name) return alert.user_name;
  const match = (alert?.title || "").match(/\(([^)]+)\)\s*$/);
  return match ? match[1] : "";
}

export default function AlertWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscribe } = useRealtime();
  const { openConversation } = useChat();
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchAlerts = useCallback(() => {
    return axios
      .post("/retrieve-alerts/", { page: 0, page_size: 10 })
      .then(({ data }) => {
        const payload = data.response?.[0] || {};
        setAlerts(payload.data || []);
        setUnviewedCount(payload.unviewed_count || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => subscribe(ALERTS_CHANNEL), [subscribe]);

  useRealtimeDomain(REALTIME_DOMAINS.ALERT, (event) => {
    if (event.type !== "alert.created" || !event.payload?.id) return;
    const incoming = event.payload;
    setAlerts((prev) => {
      if (prev.some((row) => String(row.id) === String(incoming.id))) {
        return prev;
      }
      return [incoming, ...prev].slice(0, 10);
    });
    if (!incoming.viewed) {
      setUnviewedCount((count) => count + 1);
    }
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleToggle() {
    const next = !isOpen;
    setIsOpen(next);
    if (next) fetchAlerts();
  }

  function openAlert(alert) {
    const mark = axios.post(`/alerts/${alert.id}/mark-viewed/`, {});
    setIsOpen(false);
    mark
      .then(({ data }) => {
        const payload = data.response?.[0] || {};
        if (payload.unviewed_count !== undefined) {
          setUnviewedCount(payload.unviewed_count);
        }
        setAlerts((prev) =>
          prev.map((row) =>
            String(row.id) === String(alert.id) ? { ...row, viewed: true } : row
          )
        );
      })
      .catch(() => {})
      .finally(() => {
        const conversationId = chatConversationIdFromAlert(alert);
        if (conversationId) {
          openConversation(conversationId);
          return;
        }
        const path = alertNavigationPath(alert);
        if (path) {
          navigate(path);
        }
      });
  }

  function markAllViewed() {
    setMarkingAll(true);
    axios
      .post("/mark-viewed-alerts/", { all: true })
      .then(({ data }) => {
        const payload = data.response?.[0] || {};
        setUnviewedCount(payload.unviewed_count || 0);
        setAlerts((prev) => prev.map((row) => ({ ...row, viewed: true })));
      })
      .catch(() => {})
      .finally(() => setMarkingAll(false));
  }

  const badgeLabel = unviewedCount > 99 ? "99+" : String(unviewedCount);

  return (
    <Box ref={dropdownRef} style={{ position: "relative" }}>
      <Indicator
        color="red"
        offset={4}
        size={16}
        label={badgeLabel}
        disabled={unviewedCount < 1}
      >
        <ActionIcon
          size={40}
          variant="light"
          radius="xl"
          onClick={handleToggle}
          aria-label={t("alert_plural")}
        >
          <IconBellFilled size={20} />
        </ActionIcon>
      </Indicator>

      {isOpen && (
        <Paper
          shadow="xs"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            width: 360,
            zIndex: 1000,
            marginTop: 12,
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Box p="xs" pb={0}>
            <Flex gap="xs" align="center">
              <IconBellFilled
                size={20}
                style={{ color: "var(--mantine-color-blue-5)" }}
              />
              <Text size="sm" fw={600} style={{ flexGrow: 1 }}>
                {t("alert_plural")}
              </Text>
              {unviewedCount > 0 && (
                <Button
                  variant="subtle"
                  size="compact-xs"
                  color="gray"
                  loading={markingAll}
                  onClick={markAllViewed}
                >
                  {t("mark_all_viewed")}
                </Button>
              )}
            </Flex>
          </Box>
          <Divider mt="xs" />
          <Box p="xs" style={{ maxHeight: 360, overflowY: "auto" }}>
            {alerts.length === 0 && (
              <Text size="sm" c="dimmed">
                {t("alert_empty")}
              </Text>
            )}
            {alerts.map((alert) => {
              const userName = alertUserName(alert);
              return (
              <Box
                key={alert.id}
                p="xs"
                mb={4}
                style={{
                  cursor: "pointer",
                  borderRadius: "var(--mantine-radius-sm)",
                  background: alert.viewed
                    ? "transparent"
                    : "var(--mantine-color-blue-0)",
                }}
                onClick={() => openAlert(alert)}
              >
                <Text size="sm" fw={alert.viewed ? 400 : 600}>
                  {alert.title}
                </Text>
                {userName && (
                  <Text size="xs" c="dimmed">
                    {userName}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  {alertStamp(alert.created_at)}
                </Text>
              </Box>
              );
            })}
          </Box>
          <Button
            variant="subtle"
            size="sm"
            fullWidth
            component={Link}
            to="/alerts"
            onClick={() => setIsOpen(false)}
          >
            {t("see_all")}
          </Button>
        </Paper>
      )}
    </Box>
  );
}
